import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import atributoService from '../services/atributoService';
import regimenLaboralService from '../services/regimenLaboralService';
import formulaService from '../services/formulaService';
import afpService from '../services/afpService';
import informacionGeneralService from '../services/informacionGeneralService';
import tramoRentaService from '../services/tramoRentaService';
import plameConfiguracionService from '../services/plameConfiguracionService';
import empleadoService from '../services/empleadoService';
import calculosBoletaService from '../services/calculosBoletaService';
import * as XLSX from 'xlsx';

const ImportarEmpleados = ({ onClose, onImportSuccess }) => {
  const { selectedCompany } = useAuth();
  const fileInputRef = useRef(null);
  
  // Estados para datos de las APIs
  const [datosApis, setDatosApis] = useState({
    atributos: {},
    regimenesLaborales: [],
    formulas: [],
    afps: [],
    informacionGeneral: {},
    tramosRenta: [],
    conceptosPlame: []
  });

  // Estados de carga
  const [cargandoDatos, setCargandoDatos] = useState(false);
  const [datosListos, setDatosListos] = useState(false);
  const [progresoCarga, setProgresoCarga] = useState(0);

  // Estados para el archivo
  const [archivo, setArchivo] = useState(null);
  const [procesandoArchivo, setProcesandoArchivo] = useState(false);
  const [datosExcel, setDatosExcel] = useState([]);
  const [conceptosExcel, setConceptosExcel] = useState([]);
  
  // Estados para validación e importación
  const [validaciones, setValidaciones] = useState([]);
  const [validacionesConceptos, setValidacionesConceptos] = useState([]);
  const [erroresValidacion, setErroresValidacion] = useState([]);
  const [erroresValidacionConceptos, setErroresValidacionConceptos] = useState([]);
  const [empleadosValidos, setEmpleadosValidos] = useState([]);
  const [conceptosValidos, setConceptosValidos] = useState([]);
  const [mostrarValidaciones, setMostrarValidaciones] = useState(false);
  const [importandoEmpleados, setImportandoEmpleados] = useState(false);

  // Estados para resultados
  const [resultadosImportacion, setResultadosImportacion] = useState(null);
  const [mostrarResultados, setMostrarResultados] = useState(false);
  
  // Cargar todos los datos necesarios al montar el componente
  useEffect(() => {
    if (selectedCompany?.id) {
      cargarTodosLosDatos();
    }
  }, [selectedCompany]);

  const cargarTodosLosDatos = async () => {
    setCargandoDatos(true);
    setProgresoCarga(0);
    
    try {
      console.log('🚀 Iniciando carga de todos los datos para importación...');
      
      // Array de tareas de carga
      const tareasDeCargas = [
        { nombre: 'Atributos del sistema', funcion: cargarAtributos },
        { nombre: 'Regímenes laborales', funcion: cargarRegimenesLaborales },
        { nombre: 'Fórmulas de cálculo', funcion: cargarFormulas },
        { nombre: 'Datos de AFPs', funcion: cargarAfps },
        { nombre: 'Información general (UIT/Sueldo)', funcion: cargarInformacionGeneral },
        { nombre: 'Tramos de renta', funcion: cargarTramosRenta },
        { nombre: 'Códigos PLAME', funcion: cargarCodigosPlame }
      ];

      let datosCompletos = {
        atributos: {},
        regimenesLaborales: [],
        formulas: [],
        afps: [],
        informacionGeneral: {},
        tramosRenta: [],
        conceptosPlame: []
      };

      // Ejecutar cada tarea de carga
      for (let i = 0; i < tareasDeCargas.length; i++) {
        const tarea = tareasDeCargas[i];
        console.log(`📋 Cargando: ${tarea.nombre}...`);
        
        try {
          const resultado = await tarea.funcion();
          
          // Asignar resultado según el tipo de tarea
          switch (i) {
            case 0: datosCompletos.atributos = resultado; break;
            case 1: datosCompletos.regimenesLaborales = resultado; break;
            case 2: datosCompletos.formulas = resultado; break;
            case 3: datosCompletos.afps = resultado; break;
            case 4: datosCompletos.informacionGeneral = resultado; break;
            case 5: datosCompletos.tramosRenta = resultado; break;
            case 6: datosCompletos.conceptosPlame = resultado; break;
          }
          
          console.log(`✅ ${tarea.nombre} cargado exitosamente`);
        } catch (error) {
          console.error(`❌ Error cargando ${tarea.nombre}:`, error);
        }
        
        // Actualizar progreso
        const progreso = Math.round(((i + 1) / tareasDeCargas.length) * 100);
        setProgresoCarga(progreso);
      }

      setDatosApis(datosCompletos);
      setDatosListos(true);
      console.log('✅ Todos los datos cargados exitosamente');
      console.log('📊 Resumen de datos cargados:', {
        atributos: Object.keys(datosCompletos.atributos).length,
        regimenesLaborales: datosCompletos.regimenesLaborales.length,
        formulas: datosCompletos.formulas.length,
        afps: datosCompletos.afps.length,
        informacionGeneral: Object.keys(datosCompletos.informacionGeneral).length,
        tramosRenta: datosCompletos.tramosRenta.length,
        conceptosPlame: datosCompletos.conceptosPlame.length
      });
      console.log('🔍 ESTRUCTURA COMPLETA DE ATRIBUTOS:', datosCompletos.atributos);
      console.log('🔍 CLAVES DE ATRIBUTOS DISPONIBLES:', Object.keys(datosCompletos.atributos));

    } catch (error) {
      console.error('❌ Error general cargando datos:', error);
    } finally {
      setCargandoDatos(false);
    }
  };

  // Funciones de carga individual de cada API
  const cargarAtributos = async () => {
    const response = await atributoService.obtenerConfiguracionCompleta(selectedCompany.id);
    console.log('🔍 Response completa de atributos:', response);
    console.log('🔍 Estructura de datos:', response.data);
    return response.success ? (response.data.sub_atributos || {}) : {};
  };

  const cargarRegimenesLaborales = async () => {
    const response = await regimenLaboralService.getAll();
    return response.success ? (response.data || []) : [];
  };

  const cargarFormulas = async () => {
    const response = await formulaService.obtenerFormulas();
    return response.success && response.data ? 
      (Array.isArray(response.data.data) ? response.data.data : []) : [];
  };

  const cargarAfps = async () => {
    const response = await afpService.obtenerAfps();
    return response.success && response.data ? 
      (Array.isArray(response.data) ? response.data : []) : [];
  };

  const cargarInformacionGeneral = async () => {
    const response = await informacionGeneralService.obtenerInformacionActual();
    return response.success && response.data ? response.data : {};
  };

  const cargarTramosRenta = async () => {
    const response = await tramoRentaService.obtenerTramosRenta();
    return response.success && response.data ? 
      (Array.isArray(response.data) ? response.data : []) : [];
  };

  const cargarCodigosPlame = async () => {
    const response = await plameConfiguracionService.obtenerConfiguraciones({ per_page: 1000 });
    
    if (response.success) {
      const conceptosCompletos = response.data.data.map(config => ({
        id: config.id,
        codigo: config.codigo_plame,
        descripcion: config.descripcion,
        clasificacion: config.clasificacion,
        clasificacion_id: config.clasificacion?.id || config.clasificacion_id,
        id_tipo: config.clasificacion?.id_tipo || null,
        // Incluir todas las afectaciones
        essalud_regular: config.essalud_regular,
        essalud_cbssp_seg: config.essalud_cbssp_seg,
        essalud_regular_pension: config.essalud_regular_pension,
        essalud_sctr: config.essalud_sctr,
        fondo_compl_jubil: config.fondo_compl_jubil,
        fondo_derechos_artista: config.fondo_derechos_artista,
        impuesto_extraord_solid: config.impuesto_extraord_solid,
        reg_esp_pensiones_pesq: config.reg_esp_pensiones_pesq,
        renta_5ta_categoria: config.renta_5ta_categoria,
        senati: config.senati,
        sistema_nacional_19990: config.sistema_nacional_19990,
        sistema_privado_pension: config.sistema_privado_pension,
        contrib_solidaria_assist: config.contrib_solidaria_assist
      }));
      
      console.log('✅ Códigos PLAME cargados con afectaciones:', conceptosCompletos.length);
      console.log('📋 Conceptos PLAME completos con afectaciones:', conceptosCompletos);
      
      // Mostrar ejemplo de afectaciones
      if (conceptosCompletos.length > 0) {
        const conceptoEjemplo = conceptosCompletos[0];
        console.log('🔍 Ejemplo de concepto PLAME con afectaciones:', {
          codigo: conceptoEjemplo.codigo,
          descripcion: conceptoEjemplo.descripcion,
          clasificacion_id: conceptoEjemplo.clasificacion_id,
          afectaciones: {
            essalud_regular: conceptoEjemplo.essalud_regular,
            essalud_cbssp_seg: conceptoEjemplo.essalud_cbssp_seg,
            essalud_sctr: conceptoEjemplo.essalud_sctr,
            fondo_compl_jubil: conceptoEjemplo.fondo_compl_jubil,
            fondo_derechos_artista: conceptoEjemplo.fondo_derechos_artista,
            impuesto_extraord_solid: conceptoEjemplo.impuesto_extraord_solid,
            reg_esp_pensiones_pesq: conceptoEjemplo.reg_esp_pensiones_pesq,
            renta_5ta_categoria: conceptoEjemplo.renta_5ta_categoria,
            senati: conceptoEjemplo.senati,
            sistema_nacional_19990: conceptoEjemplo.sistema_nacional_19990,
            sistema_privado_pension: conceptoEjemplo.sistema_privado_pension,
            contrib_solidaria_assist: conceptoEjemplo.contrib_solidaria_assist
          }
        });
      }
      
      return conceptosCompletos;
    }
    
    return [];
  };

  // Función para manejar la selección de archivo
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setArchivo(file);
      procesarArchivo(file);
    }
  };

  // Función para limpiar headers (remover asteriscos y espacios extra)
  const limpiarHeaders = (datos) => {
    return datos.map(fila => {
      const filaLimpia = {};
      Object.keys(fila).forEach(header => {
        // Remover asterisco y espacios extra del header
        const headerLimpio = header.replace(/\s*\*\s*$/, '').trim();
        filaLimpia[headerLimpio] = fila[header];
      });
      return filaLimpia;
    });
  };

  // Función para procesar el archivo Excel
  const procesarArchivo = async (file) => {
    setProcesandoArchivo(true);
    setDatosExcel([]);
    setConceptosExcel([]);
    setValidaciones([]);
    setValidacionesConceptos([]);
    setErroresValidacion([]);
    setErroresValidacionConceptos([]);
    
    try {
      console.log('📄 Procesando archivo:', file.name);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          
          console.log('📋 Hojas disponibles:', workbook.SheetNames);
          
          // Verificar que tenga al menos las dos hojas esperadas
          if (workbook.SheetNames.length < 2) {
            setErroresValidacion([{ 
              fila: 0, 
              error: 'El archivo debe contener al menos 2 hojas: "Empleados" y "Conceptos"' 
            }]);
            return;
          }
          
          // Procesar primera hoja (Empleados)
          const sheetEmpleados = workbook.SheetNames[0];
          const worksheetEmpleados = workbook.Sheets[sheetEmpleados];
          const datosEmpleadosRaw = XLSX.utils.sheet_to_json(worksheetEmpleados);
          
          // Procesar segunda hoja (Conceptos)
          const sheetConceptos = workbook.SheetNames[1];
          const worksheetConceptos = workbook.Sheets[sheetConceptos];
          const datosConceptosRaw = XLSX.utils.sheet_to_json(worksheetConceptos);
          
          // Limpiar headers (remover asteriscos)
          const datosEmpleados = limpiarHeaders(datosEmpleadosRaw);
          const datosConceptos = limpiarHeaders(datosConceptosRaw);
          
          console.log('📊 Headers originales de empleados:', datosEmpleadosRaw.length > 0 ? Object.keys(datosEmpleadosRaw[0]) : []);
          console.log('📊 Headers limpiados de empleados:', datosEmpleados.length > 0 ? Object.keys(datosEmpleados[0]) : []);
          console.log('📊 Headers originales de conceptos:', datosConceptosRaw.length > 0 ? Object.keys(datosConceptosRaw[0]) : []);
          console.log('📊 Headers limpiados de conceptos:', datosConceptos.length > 0 ? Object.keys(datosConceptos[0]) : []);
          console.log('📊 Datos de empleados procesados:', datosEmpleados);
          console.log('📊 Datos de conceptos procesados:', datosConceptos);
          
          setDatosExcel(datosEmpleados);
          setConceptosExcel(datosConceptos);
          
          // Ejecutar validaciones automáticamente para ambas hojas
          validarDatos(datosEmpleados);
          validarConceptos(datosConceptos, datosEmpleados);
          
        } catch (error) {
          console.error('❌ Error procesando archivo Excel:', error);
          setErroresValidacion([{ 
            fila: 0, 
            error: 'Error al procesar el archivo Excel. Verifique que sea un archivo válido.' 
          }]);
        }
      };
      
      reader.readAsBinaryString(file);
      
    } catch (error) {
      console.error('❌ Error leyendo archivo:', error);
      setErroresValidacion([{ 
        fila: 0, 
        error: 'Error al leer el archivo. Verifique que sea un archivo Excel válido.' 
      }]);
    } finally {
      setProcesandoArchivo(false);
    }
  };

  // Función para validar los datos del Excel
  const validarDatos = (datos) => {
    console.log('🔍 Iniciando validación de datos...');
    console.log('🔍 Atributos disponibles para validación:', datosApis.atributos);
    console.log('🔍 Claves disponibles:', Object.keys(datosApis.atributos));
    
    const errores = [];
    const empleadosValidos = [];
    const validacionesDetalle = [];

    datos.forEach((fila, index) => {
      const numeroFila = index + 2; // +2 porque Excel empieza en 1 y tiene header
      const erroresFila = [];
      
      // Validaciones básicas
      if (!fila.nombre_completo || fila.nombre_completo.trim() === '') {
        erroresFila.push('Nombre completo es obligatorio');
      }
      
      if (!fila.tipo_documento || fila.tipo_documento.trim() === '') {
        erroresFila.push('Tipo de documento es obligatorio');
      }
      
      if (!fila.numero_documento || fila.numero_documento.toString().trim() === '') {
        erroresFila.push('Número de documento es obligatorio');
      } else if (fila.tipo_documento === 'DNI' && fila.numero_documento.toString().length !== 8) {
        erroresFila.push('DNI debe tener exactamente 8 dígitos');
      }
      
      if (!fila.fecha_nacimiento || fila.fecha_nacimiento.toString().trim() === '') {
        erroresFila.push('Fecha de nacimiento es obligatoria');
      }
      
      if (!fila.sexo || fila.sexo.trim() === '') {
        erroresFila.push('Sexo es obligatorio');
      } else if (!['Masculino', 'Femenino'].includes(fila.sexo)) {
        erroresFila.push('Sexo debe ser "Masculino" o "Femenino"');
      }
      
      if (!fila.cargo || fila.cargo.trim() === '') {
        erroresFila.push('Cargo es obligatorio');
      } else {
        // Validar que el cargo exista en los atributos
        const cargosDisponibles = datosApis.atributos.Cargos || [];
        const cargoExiste = cargosDisponibles.some(c => c.nombre === fila.cargo);
        if (!cargoExiste) {
          erroresFila.push(`Cargo "${fila.cargo}" no existe en el sistema`);
        }
      }
      
      if (!fila.categoria || fila.categoria.trim() === '') {
        erroresFila.push('Categoría es obligatoria');
      } else {
        // Validar que la categoría exista
        const categoriasDisponibles = datosApis.atributos.Categorías || [];
        const categoriaExiste = categoriasDisponibles.some(c => c.nombre === fila.categoria);
        if (!categoriaExiste) {
          erroresFila.push(`Categoría "${fila.categoria}" no existe en el sistema`);
        }
      }
      
      if (!fila.area_trabajo || fila.area_trabajo.trim() === '') {
        erroresFila.push('Área de trabajo es obligatoria');
      } else {
        // Validar que el área de trabajo exista
        const areasDisponibles = datosApis.atributos['Area de Trabajo'] || [];
        console.log(`🔍 Validando área trabajo para "${fila.nombre_completo}":`, {
          valorExcel: fila.area_trabajo,
          areasDisponibles: areasDisponibles,
          nombresDisponibles: areasDisponibles.map(a => a.nombre)
        });
        const areaExiste = areasDisponibles.some(a => a.nombre === fila.area_trabajo);
        if (!areaExiste) {
          erroresFila.push(`Área de trabajo "${fila.area_trabajo}" no existe en el sistema`);
        }
      }
      
      if (!fila.proyecto || fila.proyecto.trim() === '') {
        erroresFila.push('Proyecto es obligatorio');
      } else {
        // Validar que el proyecto exista
        const proyectosDisponibles = datosApis.atributos.Proyectos || [];
        console.log(`🔍 Validando proyecto para "${fila.nombre_completo}":`, {
          valorExcel: fila.proyecto,
          proyectosDisponibles: proyectosDisponibles,
          nombresDisponibles: proyectosDisponibles.map(p => p.nombre)
        });
        const proyectoExiste = proyectosDisponibles.some(p => p.nombre === fila.proyecto);
        if (!proyectoExiste) {
          erroresFila.push(`Proyecto "${fila.proyecto}" no existe en el sistema`);
        }
      }
      
      if (!fila.tipo_contratacion || fila.tipo_contratacion.trim() === '') {
        erroresFila.push('Tipo de contratación es obligatorio');
      } else {
        // Validar que el tipo de contratación exista
        const tiposDisponibles = datosApis.atributos['Tipos de Contratación'] || [];
        console.log(`🔍 Validando tipo contratación para "${fila.nombre_completo}":`, {
          valorExcel: fila.tipo_contratacion,
          tiposDisponibles: tiposDisponibles,
          nombresDisponibles: tiposDisponibles.map(t => t.nombre)
        });
        const tipoExiste = tiposDisponibles.some(t => t.nombre === fila.tipo_contratacion);
        if (!tipoExiste) {
          erroresFila.push(`Tipo de contratación "${fila.tipo_contratacion}" no existe en el sistema`);
        }
      }
      
      if (!fila.estado || fila.estado.trim() === '') {
        erroresFila.push('Estado es obligatorio');
      } else if (!['Activo', 'Inactivo'].includes(fila.estado)) {
        erroresFila.push('Estado debe ser "Activo" o "Inactivo"');
      }
      
      if (!fila.regimen_laboral || fila.regimen_laboral.trim() === '') {
        erroresFila.push('Régimen laboral es obligatorio');
      } else {
        const regimenExiste = datosApis.regimenesLaborales.some(r => r.nombre === fila.regimen_laboral);
        if (!regimenExiste) {
          erroresFila.push(`Régimen laboral "${fila.regimen_laboral}" no existe en el sistema`);
        }
      }
      
      // Validar sueldo básico
      if (!fila.sueldo_basico || parseFloat(fila.sueldo_basico) <= 0) {
        erroresFila.push('Sueldo básico debe ser mayor a 0');
      }
      
      if (!fila.sistema_pensionario || fila.sistema_pensionario.trim() === '') {
        erroresFila.push('Sistema pensionario es obligatorio');
      } else if (!['AFP', 'ONP'].includes(fila.sistema_pensionario)) {
        erroresFila.push('Sistema pensionario debe ser "AFP" o "ONP"');
      }
      
      if (!fila.fecha_ingreso || fila.fecha_ingreso.toString().trim() === '') {
        erroresFila.push('Fecha de ingreso es obligatoria');
      }
      
      if (!fila.estado_civil || fila.estado_civil.trim() === '') {
        erroresFila.push('Estado civil es obligatorio');
      } else if (!['Soltero', 'Casado', 'Divorciado', 'Viudo', 'Conviviente'].includes(fila.estado_civil)) {
        erroresFila.push('Estado civil debe ser: Soltero, Casado, Divorciado, Viudo o Conviviente');
      }
      
      if (fila.numero_hijos === undefined || fila.numero_hijos === null || fila.numero_hijos === '') {
        erroresFila.push('Número de hijos es obligatorio');
      } else if (isNaN(parseInt(fila.numero_hijos)) || parseInt(fila.numero_hijos) < 0) {
        erroresFila.push('Número de hijos debe ser un número mayor o igual a 0');
      }
      
      // Validar teléfono (opcional, pero si se proporciona debe ser válido)
      if (fila.telefono && fila.telefono.toString().trim() !== '') {
        if (fila.telefono.toString().length < 7 || fila.telefono.toString().length > 15) {
          erroresFila.push('Teléfono debe tener entre 7 y 15 dígitos');
        }
      }
      
      // Validar email (opcional, pero si se proporciona debe tener formato válido)
      if (fila.email && fila.email.trim() !== '') {
        if (!/\S+@\S+\.\S+/.test(fila.email)) {
          erroresFila.push('Email no tiene formato válido');
        }
      }
      
      if (!fila.direccion || fila.direccion.trim() === '') {
        erroresFila.push('Dirección es obligatoria');
      }
      
      if (!fila.distrito || fila.distrito.trim() === '') {
        erroresFila.push('Distrito es obligatorio');
      }
      
      if (!fila.provincia || fila.provincia.trim() === '') {
        erroresFila.push('Provincia es obligatoria');
      }
      
      if (!fila.departamento || fila.departamento.trim() === '') {
        erroresFila.push('Departamento es obligatorio');
      }
      
      // Validar campos AFP solo si sistema pensionario es AFP
      if (fila.sistema_pensionario === 'AFP') {
        // Validar AFP (obligatorio cuando es AFP)
        if (!fila.afp || fila.afp.trim() === '') {
          erroresFila.push('AFP es obligatoria cuando el sistema pensionario es AFP');
        } else {
          const afpExiste = datosApis.afps.some(a => a.nombre === fila.afp);
          if (!afpExiste) {
            erroresFila.push(`AFP "${fila.afp}" no existe en el sistema`);
          }
        }
        
        // Validar tipo AFP (obligatorio cuando es AFP)
        if (!fila.tipo_afp || fila.tipo_afp.trim() === '') {
          erroresFila.push('Tipo de AFP es obligatorio cuando el sistema pensionario es AFP');
        } else if (!['Flujo', 'Mixto', 'Balance'].includes(fila.tipo_afp)) {
          erroresFila.push('Tipo de AFP debe ser "Flujo", "Mixto" o "Balance"');
        }
        
        // Validar CUSPP (obligatorio cuando es AFP)
        if (!fila.cuspp || fila.cuspp.toString().trim() === '') {
          erroresFila.push('CUSPP es obligatorio cuando el sistema pensionario es AFP');
        } else if (!/^[A-Za-z0-9]{1,12}$/.test(fila.cuspp.toString().trim())) {
          erroresFila.push('CUSPP debe tener máximo 12 caracteres alfanuméricos (letras y números)');
        }
      } else {
        // Si es ONP, validar que los campos AFP sean válidos si se proporcionan (pero no son obligatorios)
        if (fila.afp && fila.afp.trim() !== '') {
          const afpExiste = datosApis.afps.some(a => a.nombre === fila.afp);
          if (!afpExiste) {
            erroresFila.push(`AFP "${fila.afp}" no existe en el sistema (campo se ignorará porque es sistema ONP)`);
          }
        }
        if (fila.tipo_afp && fila.tipo_afp.trim() !== '' && !['Flujo', 'Mixto', 'Balance'].includes(fila.tipo_afp)) {
          erroresFila.push('Tipo de AFP debe ser "Flujo", "Mixto" o "Balance" (campo se ignorará porque es sistema ONP)');
        }
        if (fila.cuspp && fila.cuspp.toString().trim() !== '' && !/^[A-Za-z0-9]{1,12}$/.test(fila.cuspp.toString().trim())) {
          erroresFila.push('CUSPP debe tener máximo 12 caracteres alfanuméricos (campo se ignorará porque es sistema ONP)');
        }
      }
      
      // Validar hora de entrada (obligatorio)
      if (!fila.hora_entrada || fila.hora_entrada.toString().trim() === '') {
        erroresFila.push('Hora de entrada es obligatoria');
      } else {
        // Validar formato HH:MM
        const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(fila.hora_entrada.toString())) {
          erroresFila.push('Hora de entrada debe tener formato HH:MM (ej: 08:00)');
        }
      }
      
      // Agregar errores o empleado válido
      if (erroresFila.length > 0) {
        errores.push({
          fila: numeroFila,
          empleado: fila.nombre_completo || `Fila ${numeroFila}`,
          errores: erroresFila
        });
      } else {
        empleadosValidos.push({
          ...fila,
          fila: numeroFila
        });
      }
      
      // Agregar detalle de validación
      validacionesDetalle.push({
        fila: numeroFila,
        empleado: fila.nombre_completo || `Fila ${numeroFila}`,
        estado: erroresFila.length > 0 ? 'error' : 'valido',
        errores: erroresFila
      });
    });

    setErroresValidacion(errores);
    setEmpleadosValidos(empleadosValidos);
    setValidaciones(validacionesDetalle);
    setMostrarValidaciones(true);
    
    console.log('📊 Resultado de validaciones empleados:', {
      totalFilas: datos.length,
      empleadosValidos: empleadosValidos.length,
      empleadosConErrores: errores.length
    });
  };

  // Función para validar los conceptos del Excel
  const validarConceptos = (conceptos, datosEmpleados = null) => {
    console.log('🔍 Iniciando validación de conceptos...');
    console.log('🔍 Conceptos:', conceptos);
    console.log('🔍 Datos empleados recibidos:', datosEmpleados);
    
    const errores = [];
    const conceptosValidosTemp = [];
    const validacionesDetalle = [];

    conceptos.forEach((fila, index) => {
      const numeroFila = index + 2; // +2 porque Excel empieza en 1 y tiene header
      const erroresFila = [];
      
      // Validar DNI (obligatorio)
      if (!fila.DNI || fila.DNI.toString().trim() === '') {
        erroresFila.push('DNI es obligatorio');
      } else if (fila.DNI.toString().length !== 8) {
        erroresFila.push('DNI debe tener exactamente 8 dígitos');
      }
      
      console.log('🔍 Conceptos PLAME:', datosApis.conceptosPlame);
      console.log('🔍 Código PLAME:', fila.codigo_plame.toString().trim());
      console.log('🔍 Concepto:', datosApis.conceptosPlame.find(concepto => concepto.codigo === fila.codigo_plame.toString().trim()));
      // Validar código PLAME (obligatorio)
      if (!fila.codigo_plame || fila.codigo_plame.toString().trim() === '') {
        erroresFila.push('Código PLAME es obligatorio');
      } else {
        // Verificar que el código PLAME exista en el sistema
        const conceptoEncontrado = datosApis.conceptosPlame.find(concepto => 
          concepto.codigo === fila.codigo_plame.toString().trim()
        );
        if (!conceptoEncontrado) {
          erroresFila.push(`Código PLAME "${fila.codigo_plame}" no existe en el sistema`);
        } else {
          console.log('✅ Concepto PLAME encontrado:', conceptoEncontrado);
        }
      }
      
      // Validar monto (obligatorio y mayor a 0)
      if (!fila.monto || isNaN(parseFloat(fila.monto)) || parseFloat(fila.monto) <= 0) {
        erroresFila.push('Monto debe ser un número mayor a 0');
      }
      
              // Verificar que el DNI corresponda a un empleado en la primera hoja
        const empleadosParaValidar = datosEmpleados || datosExcel;
        console.log('🔍 Datos empleados para validar:', empleadosParaValidar);
        console.log('🔍 DNI a buscar:', fila.DNI);
        console.log('🔍 Empleado encontrado:', empleadosParaValidar.find(emp => emp.numero_documento?.toString() === fila.DNI?.toString()));
        const empleadoExiste = empleadosParaValidar.some(emp => emp.numero_documento?.toString() === fila.DNI?.toString());
        if (!empleadoExiste && fila.DNI) {
          erroresFila.push(`No se encontró empleado con DNI "${fila.DNI}" en la hoja de empleados`);
        }
      
      // Agregar errores o concepto válido
      if (erroresFila.length > 0) {
        errores.push({
          fila: numeroFila,
          dni: fila.DNI || `Fila ${numeroFila}`,
          concepto: fila.codigo_plame || 'Sin código',
          errores: erroresFila
        });
      } else {
        conceptosValidosTemp.push({
          ...fila,
          fila: numeroFila,
          DNI: fila.DNI.toString().trim(),
          codigo_plame: fila.codigo_plame.toString().trim(),
          monto: parseFloat(fila.monto)
        });
      }
      
      // Agregar detalle de validación
      validacionesDetalle.push({
        fila: numeroFila,
        dni: fila.DNI || `Fila ${numeroFila}`,
        concepto: fila.codigo_plame || 'Sin código',
        estado: erroresFila.length > 0 ? 'error' : 'valido',
        errores: erroresFila
      });
    });

    setErroresValidacionConceptos(errores);
    setConceptosValidos(conceptosValidosTemp);
    setValidacionesConceptos(validacionesDetalle);
    
    console.log('📊 Resultado de validaciones conceptos:', {
      totalFilas: conceptos.length,
      conceptosValidos: conceptosValidosTemp.length,
      conceptosConErrores: errores.length
    });
  };

  // Función para calcular conceptos automáticamente usando el service de cálculo de boletas
  // NOTA: Esta función ya incluye los conceptos del Excel en el cálculo, no hay necesidad de agregarlos por separado
  const calcularConceptosAutomaticos = async (empleadoData) => {
    try {
      console.log(`🧮 Calculando conceptos automáticos para: ${empleadoData.nombre_completo}`);
      
      // Obtener conceptos del Excel para este empleado específico
      const conceptosEmpleadoExcel = conceptosValidos.filter(concepto => 
        concepto.DNI === empleadoData.numero_documento?.toString().trim()
      );

      // Convertir conceptos del Excel al formato que espera el service
      const ingresosDelExcel = conceptosEmpleadoExcel.map(concepto => {
        // Buscar el concepto PLAME para obtener el objeto completo
        const conceptoPlame = datosApis.conceptosPlame.find(cp => cp.codigo === concepto.codigo_plame);
        
        return {
          codigoPlame: concepto.codigo_plame,
          concepto: conceptoPlame?.descripcion || 'Concepto del Excel',
          id: conceptoPlame?.id || null,
          monto: concepto.monto,
          plame_configuracion_id: conceptoPlame?.id || null,
          tipo_concepto: 'ingreso'
        };
      });

      console.log(`📋 Conceptos del Excel para ${empleadoData.nombre_completo}:`, ingresosDelExcel);
      
      // Armar la estructura de datos que espera el service
      const datosCompletos = {
        datosEmpresa: {
          id: selectedCompany.id,
          nombre: selectedCompany.nombre || 'Empresa Seleccionada'
        },
        datosAPIs: {
          regimenesLaborales: datosApis.regimenesLaborales,
          regimenSeleccionado: datosApis.regimenesLaborales.find(r => r.nombre === empleadoData.regimen_laboral),
          conceptosPlame: datosApis.conceptosPlame,
          conceptosIngresos: datosApis.conceptosPlame.filter(c => c.id_tipo === 1),
          conceptosDescuentos: datosApis.conceptosPlame.filter(c => c.id_tipo === 2),
          conceptosAportes: datosApis.conceptosPlame.filter(c => c.id_tipo === 3),
          formulas: datosApis.formulas,
          formulasIngresos: Array.isArray(datosApis.formulas) ? datosApis.formulas.filter(f => f.id_tipo === 1) : [],
          formulasDescuentos: Array.isArray(datosApis.formulas) ? datosApis.formulas.filter(f => f.id_tipo === 2) : [],
          formulasAportes: Array.isArray(datosApis.formulas) ? datosApis.formulas.filter(f => f.id_tipo === 3) : [],
          afps: datosApis.afps,
          uit: datosApis.informacionGeneral.uit || null,
          sueldoMinimo: datosApis.informacionGeneral.sueldo_minimo || null,
          informacionGeneral: datosApis.informacionGeneral,
          tramosRenta: datosApis.tramosRenta,
          atributosData: datosApis.atributos
        },
        datosSalariales: {
          sueldoBasico: parseFloat(empleadoData.sueldo_basico) || 0,
          sistemaPensionario: empleadoData.sistema_pensionario || 'ONP',
          afp: empleadoData.afp || null,
          tipoAfp: empleadoData.tipo_afp || 'Flujo',
          cuspp: empleadoData.cuspp || null,
          regimenLaboral: empleadoData.regimen_laboral,
          tipoContratacion: empleadoData.tipo_contratacion
        },
        conceptosUsuario: {
          ingresos: ingresosDelExcel, // ✅ Incluir conceptos del Excel
          descuentos: [],
          aportes: []
        },
        datosEmpleado: {
          nombre_completo: empleadoData.nombre_completo,
          numero_documento: empleadoData.numero_documento,
          fecha_nacimiento: empleadoData.fecha_nacimiento,
          sexo: empleadoData.sexo,
          estado_civil: empleadoData.estado_civil,
          numero_hijos: parseInt(empleadoData.numero_hijos) || 0,
          email: empleadoData.email,
          telefono: empleadoData.telefono,
          fecha_ingreso: empleadoData.fecha_ingreso
        }
      };

      console.log('📊 Datos enviados al service de cálculo:', datosCompletos);
      
      // Llamar al service de cálculo de boletas
      const resultadoCalculos = await calculosBoletaService.generarCalculos(
        datosCompletos, 
        'importacion_empleados_excel'
      );
      
      console.log('✅ Resultados del cálculo de boletas:', resultadoCalculos);

      // Procesar los resultados y convertirlos al formato esperado para el backend
      const conceptosCalculados = [];
      
      if (resultadoCalculos && resultadoCalculos.success) {
        const { dataIngresos, dataDescuentos, dataAportes } = resultadoCalculos.data || {};
        
        // Procesar ingresos
        if (dataIngresos && Array.isArray(dataIngresos)) {
          dataIngresos.forEach(ingreso => {
            if (ingreso.id_concepto && ingreso.valor > 0) {
              conceptosCalculados.push({
                plame_configuracion_id: ingreso.id_concepto.id || null,
                tipo_concepto: 'ingreso',
                monto: parseFloat(ingreso.valor)
              });
            }
          });
        }
        
        // Procesar descuentos
        if (dataDescuentos && Array.isArray(dataDescuentos)) {
          dataDescuentos.forEach(descuento => {
            if (descuento.id_concepto && descuento.valor > 0) {
              conceptosCalculados.push({
                plame_configuracion_id: descuento.id_concepto.id || null,
                tipo_concepto: 'descuento',
                monto: parseFloat(descuento.valor)
              });
            }
          });
        }
        
        // Procesar aportes
        if (dataAportes && Array.isArray(dataAportes)) {
          dataAportes.forEach(aporte => {
            if (aporte.id_concepto && aporte.valor > 0) {
              conceptosCalculados.push({
                plame_configuracion_id: aporte.id_concepto.id || null,
                tipo_concepto: 'aporte',
                monto: parseFloat(aporte.valor)
              });
            }
          });
        }
      }
      
      console.log(`💰 Conceptos finales para ${empleadoData.nombre_completo}:`, {
        total: conceptosCalculados.length,
        conceptosDelExcel: conceptosEmpleadoExcel.length,
        nota: 'El total incluye conceptos automáticos + conceptos del Excel'
      });
      console.log(`📋 Detalle de conceptos:`, conceptosCalculados);
      return conceptosCalculados;
      
    } catch (error) {
      console.error(`❌ Error calculando conceptos para ${empleadoData.nombre_completo}:`, error);
      return []; // Retornar array vacío en caso de error
    }
  };

  // Función para importar empleados válidos
  const importarEmpleados = async () => {
    if (empleadosValidos.length === 0) return;
    
    setImportandoEmpleados(true);
    const resultados = {
      exitosos: [],
      fallidos: []
    };

    try {
      console.log('🚀 Iniciando importación REAL de empleados válidos en base de datos...');
      
      for (const empleadoData of empleadosValidos) {
        try {
          console.log(`📤 Procesando empleado: ${empleadoData.nombre_completo}`);
          
          // Calcular conceptos automáticamente usando el service de cálculo de boletas
          // NOTA: Los conceptos del Excel ya están incluidos en conceptosCalculados
          const conceptosCalculados = await calcularConceptosAutomaticos(empleadoData);
          
          // Los conceptos para el backend son solo los calculados (que ya incluyen los del Excel)
          const conceptosParaBackend = conceptosCalculados;
          
          // Desglose detallado de conceptos por tipo (incluye conceptos calculados + conceptos del Excel)
          const conteoConceptos = {
            ingresos: conceptosCalculados.filter(c => c.tipo_concepto === 'ingreso').length,
            descuentos: conceptosCalculados.filter(c => c.tipo_concepto === 'descuento').length,
            aportes: conceptosCalculados.filter(c => c.tipo_concepto === 'aporte').length
          };
          
          // Obtener la cantidad de conceptos del Excel para este empleado (solo para reporte)
          const conceptosEmpleadoExcel = conceptosValidos.filter(concepto => 
            concepto.DNI === empleadoData.numero_documento?.toString().trim()
          );
          
          console.log(`💰 Total conceptos para ${empleadoData.nombre_completo}:`, {
            conceptos: {
              total: conceptosCalculados.length,
              desglose: conteoConceptos
            },
            conceptosDelExcel: conceptosEmpleadoExcel.length,
            nota: 'Los conceptos del Excel ya están incluidos en el total (no se duplican)'
          });

          // Preparar datos del empleado para el backend
          const empleadoParaBackend = {
            empresa_id: selectedCompany.id,
            nombre_completo: empleadoData.nombre_completo ? empleadoData.nombre_completo.toString().trim() : null,
            tipo_documento: empleadoData.tipo_documento || 'DNI',
            numero_documento: empleadoData.numero_documento ? empleadoData.numero_documento.toString().trim() : null,
            fecha_nacimiento: empleadoData.fecha_nacimiento || null,
            sexo: empleadoData.sexo ? empleadoData.sexo.toString().trim() : null,
            estado_civil: empleadoData.estado_civil ? empleadoData.estado_civil.toString().trim() : null,
            numero_hijos: parseInt(empleadoData.numero_hijos) || 0,
            telefono: empleadoData.telefono ? empleadoData.telefono.toString().trim() : null,
            email: empleadoData.email ? empleadoData.email.toString().trim() : null,
            direccion: empleadoData.direccion ? empleadoData.direccion.toString().trim() : null,
            distrito: empleadoData.distrito ? empleadoData.distrito.toString().trim() : null,
            provincia: empleadoData.provincia ? empleadoData.provincia.toString().trim() : null,
            departamento: empleadoData.departamento ? empleadoData.departamento.toString().trim() : null,
            cargo: empleadoData.cargo ? empleadoData.cargo.toString().trim() : null,
            categoria: empleadoData.categoria ? empleadoData.categoria.toString().trim() : null,
            areas_de_trabajo: empleadoData.area_trabajo ? empleadoData.area_trabajo.toString().trim() : null,
            proyecto: empleadoData.proyecto ? empleadoData.proyecto.toString().trim() : null,
            tipo_contratacion: empleadoData.tipo_contratacion ? empleadoData.tipo_contratacion.toString().trim() : null,
            estado: empleadoData.estado || 'Activo',
            regimen_laboral_id: obtenerIdRegimenLaboral(empleadoData.regimen_laboral),
            sueldo_basico: parseFloat(empleadoData.sueldo_basico) || 0,
            sistema_pensionario: empleadoData.sistema_pensionario || 'ONP',
            afp_id: empleadoData.sistema_pensionario === 'AFP' ? obtenerIdAfp(empleadoData.afp) : null,
            cuspp: empleadoData.sistema_pensionario === 'AFP' ? (empleadoData.cuspp ? empleadoData.cuspp.toString().trim() : null) : null,
            tipo_afp: empleadoData.tipo_afp || 'Flujo', // Siempre enviar 'Flujo' por defecto (incluso para ONP)
            hora_entrada: empleadoData.hora_entrada ? empleadoData.hora_entrada.toString().trim() : '08:00',
            hora_salida: empleadoData.hora_salida ? empleadoData.hora_salida.toString().trim() : '17:00',
            cuenta_bancaria: empleadoData.cuenta_bancaria ? empleadoData.cuenta_bancaria.toString().trim() : null,
            banco: empleadoData.banco ? empleadoData.banco.toString().trim() : null,
            cci: empleadoData.cci ? empleadoData.cci.toString().trim() : null,
            contacto_emergencia: empleadoData.contacto_emergencia ? empleadoData.contacto_emergencia.toString().trim() : null,
            telefono_emergencia: empleadoData.telefono_emergencia ? empleadoData.telefono_emergencia.toString().trim() : null,
            observaciones: empleadoData.observaciones ? empleadoData.observaciones.toString().trim() : null,
            fecha_ingreso: empleadoData.fecha_ingreso || new Date().toISOString().split('T')[0],
            conceptos: conceptosParaBackend // Incluir conceptos al crear empleado
          };

          console.log(`📤 Importando empleado: ${empleadoData.nombre_completo}`);
          console.log(`📊 Con ${conceptosParaBackend.length} conceptos total (calculados + Excel):`, conceptosParaBackend);
          console.log(`📋 Datos enviados al backend:`, empleadoParaBackend);
          
          // Llamada real al backend
          const response = await empleadoService.createEmpleado(empleadoParaBackend);
          
          resultados.exitosos.push({
            fila: empleadoData.fila,
            nombre: empleadoData.nombre_completo,
            id: response.data?.id,
            conceptos: {
              total: conceptosParaBackend.length,
              ingresos: conteoConceptos.ingresos,
              descuentos: conteoConceptos.descuentos,
              aportes: conteoConceptos.aportes,
              conceptosDelExcel: conceptosEmpleadoExcel.length
            }
          });
          
        } catch (error) {
          console.error(`❌ Error importando empleado ${empleadoData.nombre_completo}:`, error);
          resultados.fallidos.push({
            fila: empleadoData.fila,
            nombre: empleadoData.nombre_completo,
            error: error.message || 'Error desconocido'
          });
        }
      }

      setResultadosImportacion(resultados);
      setMostrarResultados(true);
      
      console.log('🎉 Importación REAL completada exitosamente en base de datos:', resultados);
      
      // Si hay empleados exitosos, notificar al componente padre
      if (resultados.exitosos.length > 0 && onImportSuccess) {
        onImportSuccess(resultados);
      }

    } catch (error) {
      console.error('❌ Error general en importación:', error);
    } finally {
      setImportandoEmpleados(false);
    }
  };

  // Funciones auxiliares
  const obtenerIdRegimenLaboral = (nombreRegimen) => {
    if (!nombreRegimen) return null;
    const regimen = datosApis.regimenesLaborales.find(r => r.nombre === nombreRegimen);
    return regimen?.id || null;
  };

  const obtenerIdAfp = (nombreAfp) => {
    if (!nombreAfp) return null;
    const afp = datosApis.afps.find(a => a.nombre === nombreAfp);
    return afp?.id || null;
  };

  // Función para descargar plantilla Excel
  const descargarPlantilla = () => {
    // Datos de ejemplo para la hoja de empleados (con asteriscos en campos obligatorios)
    const plantillaEmpleados = [{
      'nombre_completo *': 'Juan Pérez García',
      'tipo_documento *': 'DNI',
      'numero_documento *': '12345678',
      'fecha_nacimiento *': '1990-01-15',
      'sexo *': 'Masculino',
      'estado_civil *': 'Soltero',
      'numero_hijos *': 0,
      'telefono': '987654321',
      'email': 'juan.perez@email.com',
      'direccion *': 'Av. Ejemplo 123',
      'distrito *': 'Lima',
      'provincia *': 'Lima',
      'departamento *': 'Lima',
      'cargo *': datosApis.atributos.Cargos?.[0]?.nombre || 'Gerente',
      'categoria *': datosApis.atributos.Categorías?.[0]?.nombre || 'Administrativos',
      'area_trabajo *': datosApis.atributos['Area de Trabajo']?.[0]?.nombre || 'Administración',
      'proyecto *': datosApis.atributos.Proyectos?.[0]?.nombre || 'CC1',
      'tipo_contratacion *': datosApis.atributos['Tipos de Contratación']?.[0]?.nombre || 'Contrato a Plazo Indeterminado',
      'estado *': 'Activo',
      'regimen_laboral *': datosApis.regimenesLaborales[0]?.nombre || 'Régimen General',
      'sueldo_basico *': 3000,
      'sistema_pensionario *': 'ONP',
      'afp': '',
      'tipo_afp': 'Flujo',
      'cuspp': '',
      'hora_entrada *': '08:00',
      'hora_salida': '17:00',
      'cuenta_bancaria': '1234567890',
      'banco': 'BCP',
      'cci': '00211234567890123456',
      'contacto_emergencia': 'María García',
      'telefono_emergencia': '987654322',
      'observaciones': 'Empleado de ejemplo',
      'fecha_ingreso *': '2024-01-01'
    }, {
      'nombre_completo *': 'Ana López Silva',
      'tipo_documento *': 'DNI',
      'numero_documento *': '87654321',
      'fecha_nacimiento *': '1985-06-20',
      'sexo *': 'Femenino',
      'estado_civil *': 'Casado',
      'numero_hijos *': 2,
      'telefono': '912345678',
      'email': 'ana.lopez@email.com',
      'direccion *': 'Jr. Los Pinos 456',
      'distrito *': 'San Isidro',
      'provincia *': 'Lima',
      'departamento *': 'Lima',
      'cargo *': datosApis.atributos.Cargos?.[1]?.nombre || 'Supervisor',
      'categoria *': datosApis.atributos.Categorías?.[0]?.nombre || 'Administrativos',
      'area_trabajo *': datosApis.atributos['Area de Trabajo']?.[1]?.nombre || 'Operaciones',
      'proyecto *': datosApis.atributos.Proyectos?.[0]?.nombre || 'CC1',
      'tipo_contratacion *': datosApis.atributos['Tipos de Contratación']?.[0]?.nombre || 'Contrato a Plazo Indeterminado',
      'estado *': 'Activo',
      'regimen_laboral *': datosApis.regimenesLaborales[0]?.nombre || 'Régimen General',
      'sueldo_basico *': 2500,
      'sistema_pensionario *': 'AFP',
      'afp *': datosApis.afps[0]?.nombre || 'INTEGRA',
      'tipo_afp *': 'Flujo',
      'cuspp *': '1234567890123',
      'hora_entrada *': '08:30',
      'hora_salida': '17:30',
      'cuenta_bancaria': '0987654321',
      'banco': 'BBVA',
      'cci': '01198765432109876543',
      'contacto_emergencia': 'Carlos López',
      'telefono_emergencia': '987654323',
      'observaciones': 'Segundo empleado de ejemplo',
      'fecha_ingreso *': '2024-01-01'
    }];

    // Datos de ejemplo para la hoja de conceptos (todos los campos son obligatorios)
    const plantillaConceptos = [
      {
        'DNI *': '12345678',
        'codigo_plame *': datosApis.conceptosPlame[0]?.codigo || '0100',
        'monto *': 500
      },
      {
        'DNI *': '12345678',
        'codigo_plame *': datosApis.conceptosPlame[1]?.codigo || '0200',
        'monto *': 300
      },
      {
        'DNI *': '87654321',
        'codigo_plame *': datosApis.conceptosPlame[0]?.codigo || '0100',
        'monto *': 400
      },
      {
        'DNI *': '87654321',
        'codigo_plame *': datosApis.conceptosPlame[2]?.codigo || '0300',
        'monto *': 250
      }
    ];

    // Crear el workbook
    const wb = XLSX.utils.book_new();
    
    // Crear y agregar hoja de empleados
    const wsEmpleados = XLSX.utils.json_to_sheet(plantillaEmpleados);
    XLSX.utils.book_append_sheet(wb, wsEmpleados, 'Empleados');
    
    // Crear y agregar hoja de conceptos
    const wsConceptos = XLSX.utils.json_to_sheet(plantillaConceptos);
    XLSX.utils.book_append_sheet(wb, wsConceptos, 'Conceptos');
    
    // Descargar el archivo
    XLSX.writeFile(wb, 'plantilla_importacion_empleados.xlsx');
    
    console.log('📁 Plantilla descargada: plantilla_importacion_empleados.xlsx');
    console.log('📋 Contiene 2 hojas: Empleados y Conceptos');
    console.log('✨ Campos obligatorios marcados con asterisco (*):');
    console.log('   👤 Empleados: nombre_completo*, tipo_documento*, numero_documento*, fecha_nacimiento*, sexo*, estado_civil*, numero_hijos*, direccion*, distrito*, provincia*, departamento*, cargo*, categoria*, area_trabajo*, proyecto*, tipo_contratacion*, estado*, regimen_laboral*, sueldo_basico*, sistema_pensionario*, hora_entrada*, fecha_ingreso*');
    console.log('   📊 Si sistema_pensionario = AFP: afp*, tipo_afp*, cuspp* (máximo 12 caracteres alfanuméricos, solo obligatorios para AFP)');
    console.log('   📞 Campos opcionales: telefono, email (se valida formato si se proporcionan)');
    console.log('   💰 Conceptos: DNI*, codigo_plame*, monto*');
    console.log('🧮 IMPORTANTE: Los conceptos se calcularán automáticamente según la configuración de cada empleado');
    console.log('📋 Los conceptos del Excel son ADICIONALES a los calculados automáticamente');
    console.log('🏷️  CLASIFICACIÓN: Los conceptos se clasifican automáticamente como ingreso/descuento/aporte según su código PLAME');
    console.log('🔧 El sistema procesará automáticamente los headers limpiando los asteriscos para las validaciones');
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900">Importar Empleados</h3>
              <p className="text-gray-600 mt-1">Importar empleados desde archivo Excel</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Estado de carga de datos */}
          {cargandoDatos && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                <h4 className="text-lg font-medium text-blue-900">Cargando datos del sistema...</h4>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${progresoCarga}%` }}
                ></div>
              </div>
              <p className="text-blue-700 text-sm">{progresoCarga}% completado</p>
            </div>
          )}

          {/* Sección principal cuando los datos están listos */}
          {datosListos && (
            <>
              {/* Información de datos cargados */}
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <h4 className="text-lg font-medium text-green-900">Datos del sistema cargados</h4>
                </div>
                <div className="flex items-center justify-end mb-3">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    ✅ 7/7 APIs cargadas
                  </div>
                </div>
                <div className="border-t border-green-200 pt-3 text-xs text-green-700">
                  <p className="font-medium mb-1">📋 Campos obligatorios en tu archivo Excel:</p>
                  <div className="grid grid-cols-1 gap-2">
                    <div>
                      <span className="font-medium">Hoja Empleados:</span> nombre_completo*, tipo_documento*, numero_documento*, fecha_nacimiento*, sexo*, estado_civil*, numero_hijos*, direccion*, distrito*, provincia*, departamento*, cargo*, categoria*, area_trabajo*, proyecto*, tipo_contratacion*, estado*, regimen_laboral*, sueldo_basico*, sistema_pensionario*, hora_entrada*, fecha_ingreso*
                      <div className="text-xs text-blue-600 mt-1">
                        <span className="font-medium">Si AFP:</span> afp*, tipo_afp*, cuspp* (máximo 12 caracteres alfanuméricos, solo obligatorios cuando sistema_pensionario = AFP)
                      </div>
                      <div className="text-xs text-green-600 mt-1">
                        <span className="font-medium">Opcionales:</span> telefono, email (se valida formato si se proporcionan)
                      </div>
                    </div>
                    <div>
                                              <span className="font-medium">Hoja Conceptos:</span> DNI*, codigo_plame*, monto* (se clasifican automáticamente según el tipo PLAME)
                    </div>
                  </div>
                </div>
              </div>

              {/* Sección de carga de archivo */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Seleccionar archivo Excel</h4>
                    <div className="text-sm text-gray-600 mt-1 space-y-1">
                      <p>El archivo debe tener 2 hojas: "Empleados" y "Conceptos"</p>
                      <p className="text-xs">Los campos obligatorios están marcados con asterisco (*) en la plantilla</p>
                      <p className="flex items-center">
                        <span className="text-red-500 mr-1">*</span>
                        Los campos marcados con asterisco son obligatorios
                      </p>
                      <p className="flex items-center text-green-600">
                        <span className="text-green-500 mr-1">📞</span>
                        telefono y email son opcionales (se valida formato si se proporcionan)
                      </p>
                      <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                        <p className="text-xs text-blue-700 flex items-center">
                          <span className="mr-1">🧮</span>
                          Los conceptos se calcularán automáticamente según la configuración del empleado
                        </p>
                        <p className="text-xs text-blue-700 flex items-center mt-1">
                          <span className="mr-1">🏷️</span>
                          Los conceptos se clasifican automáticamente como ingresos, descuentos o aportes según su código PLAME
                        </p>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                        <p className="text-xs text-yellow-700 flex items-center">
                          <span className="mr-1">⚠️</span>
                          Los campos AFP, tipo_afp y cuspp (máximo 12 caracteres alfanuméricos) solo son obligatorios cuando sistema_pensionario = "AFP"
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={descargarPlantilla}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Descargar Plantilla (2 hojas)
                  </button>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {!archivo ? (
                    <div>
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h5 className="text-lg font-medium text-gray-900 mb-2">
                        Selecciona un archivo Excel
                      </h5>
                      <p className="text-gray-500 mb-4">
                        Soporta archivos .xlsx y .xls
                      </p>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Seleccionar archivo
                      </button>
                    </div>
                  ) : (
                    <div>
                      <svg className="w-12 h-12 mx-auto mb-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h5 className="text-lg font-medium text-gray-900 mb-2">
                        {archivo.name}
                      </h5>
                      <p className="text-gray-500 mb-4">
                        {(archivo.size / 1024).toFixed(2)} KB
                      </p>
                      <div className="flex justify-center space-x-3">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Cambiar archivo
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {procesandoArchivo && (
                  <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600 mr-3"></div>
                      <span className="text-yellow-800">Procesando archivo Excel...</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Sección de validaciones */}
              {mostrarValidaciones && (
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Resultado de Validaciones</h4>
                  
                  {/* Resumen */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Resumen Empleados */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-3">📋 Hoja de Empleados</h5>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-blue-50 border border-blue-200 rounded p-2">
                          <div className="text-lg font-bold text-blue-600">{datosExcel.length}</div>
                          <div className="text-xs text-blue-700">Total</div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded p-2">
                          <div className="text-lg font-bold text-green-600">{empleadosValidos.length}</div>
                          <div className="text-xs text-green-700">Válidos</div>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded p-2">
                          <div className="text-lg font-bold text-red-600">{erroresValidacion.length}</div>
                          <div className="text-xs text-red-700">Errores</div>
                        </div>
                      </div>
                    </div>

                    {/* Resumen Conceptos */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-3">💰 Hoja de Conceptos</h5>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-blue-50 border border-blue-200 rounded p-2">
                          <div className="text-lg font-bold text-blue-600">{conceptosExcel.length}</div>
                          <div className="text-xs text-blue-700">Total</div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded p-2">
                          <div className="text-lg font-bold text-green-600">{conceptosValidos.length}</div>
                          <div className="text-xs text-green-700">Válidos</div>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded p-2">
                          <div className="text-lg font-bold text-red-600">{erroresValidacionConceptos.length}</div>
                          <div className="text-xs text-red-700">Errores</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lista de errores */}
                  {(erroresValidacion.length > 0 || erroresValidacionConceptos.length > 0) && (
                    <div className="mb-6 space-y-4">
                      {/* Errores de empleados */}
                      {erroresValidacion.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h5 className="font-medium text-red-900 mb-3">❌ Errores en hoja de Empleados:</h5>
                          <div className="max-h-64 overflow-y-auto space-y-3">
                            {erroresValidacion.map((error, index) => (
                              <div key={index} className="bg-white border border-red-200 rounded p-3">
                                <div className="font-medium text-red-800">
                                  Fila {error.fila}: {error.empleado}
                                </div>
                                <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                                  {error.errores.map((err, errIndex) => (
                                    <li key={errIndex}>{err}</li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Errores de conceptos */}
                      {erroresValidacionConceptos.length > 0 && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <h5 className="font-medium text-orange-900 mb-3">⚠️ Errores en hoja de Conceptos:</h5>
                          <div className="max-h-64 overflow-y-auto space-y-3">
                            {erroresValidacionConceptos.map((error, index) => (
                              <div key={index} className="bg-white border border-orange-200 rounded p-3">
                                <div className="font-medium text-orange-800">
                                  Fila {error.fila}: DNI {error.dni} - Concepto {error.concepto}
                                </div>
                                <ul className="mt-2 text-sm text-orange-700 list-disc list-inside">
                                  {error.errores.map((err, errIndex) => (
                                    <li key={errIndex}>{err}</li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Botón para importar empleados válidos */}
                  {empleadosValidos.length > 0 && (
                    <div className="flex flex-col items-center space-y-4">
                      {/* Información de importación */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                        <h6 className="font-medium text-blue-900 mb-2">🚀 Resumen de Importación</h6>
                        <div className="text-sm text-blue-800 space-y-1">
                          <div>✅ {empleadosValidos.length} empleados listos para importar</div>
                          <div>🧮 Conceptos se calcularán automáticamente según configuración</div>
                          <div>📋 {conceptosValidos.length} conceptos adicionales del Excel (clasificados automáticamente)</div>
                          <div className="bg-green-100 border border-green-300 rounded p-2 mt-2">
                            <span className="text-green-800 font-medium">🚀 IMPORTACIÓN REAL: </span>
                            <span className="text-green-700">Los empleados se crearán en la base de datos con todos sus conceptos</span>
                          </div>
                          {conceptosExcel.length > 0 && erroresValidacionConceptos.length > 0 && (
                            <div className="text-orange-700">
                              ⚠️ {erroresValidacionConceptos.length} conceptos del Excel serán omitidos por errores
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Botón de importar */}
                      <button
                        onClick={importarEmpleados}
                        disabled={importandoEmpleados || (erroresValidacion.length > 0)}
                        className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {importandoEmpleados ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                            Importando empleados a la base de datos...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                            </svg>
                            🚀 Importar {empleadosValidos.length} empleados con conceptos calculados
                          </>
                        )}
                      </button>

                      {/* Nota sobre errores */}
                      {erroresValidacion.length > 0 && (
                        <div className="text-sm text-red-600 text-center">
                          ❌ Corrige los errores de empleados antes de importar
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Resultados de importación */}
              {mostrarResultados && resultadosImportacion && (
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">✅ Resultados de Importación en Base de Datos</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Empleados importados exitosamente */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-900 mb-3">
                        Importados exitosamente ({resultadosImportacion.exitosos.length})
                      </h5>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {resultadosImportacion.exitosos.map((emp, index) => (
                          <div key={index} className="text-sm text-green-700">
                            <span className="font-medium">Fila {emp.fila}:</span> {emp.nombre}
                            {typeof emp.conceptos === 'object' ? (
                              <div className="text-green-600 ml-2 mt-1">
                                <div>📊 {emp.conceptos.total} conceptos total</div>
                                <div className="text-xs">
                                  🧮 {emp.conceptos.calculados} automáticos + 📋 {emp.conceptos.excel} Excel
                                </div>
                              </div>
                            ) : emp.conceptos > 0 ? (
                              <span className="text-green-600 ml-2">({emp.conceptos} conceptos)</span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Empleados con errores */}
                    {resultadosImportacion.fallidos.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h5 className="font-medium text-red-900 mb-3">
                          Fallidos ({resultadosImportacion.fallidos.length})
                        </h5>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {resultadosImportacion.fallidos.map((emp, index) => (
                            <div key={index} className="text-sm">
                              <div className="font-medium text-red-800">Fila {emp.fila}: {emp.nombre}</div>
                              <div className="text-red-600">{emp.error}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Botones del footer */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportarEmpleados; 